import { useEffect, useMemo, useRef, useState } from "react";
import { Bell, UserPlus, Gift, Calendar, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";

const STORAGE_KEY = "lma_notifications_last_read";

type Notification = {
  id: string;
  type: "member" | "benefit" | "event";
  title: string;
  description: string;
  at: string;
  href: string;
};

async function fetchNotifications(): Promise<Notification[]> {
  const since = new Date(Date.now() - 14 * 24 * 3600 * 1000).toISOString();
  const [members, benefits, regs] = await Promise.all([
    supabase
      .from("members")
      .select("id, first_name, last_name, created_at")
      .gte("created_at", since)
      .order("created_at", { ascending: false })
      .limit(20),
    supabase
      .from("benefit_logs" as any)
      .select("id, benefit_label, redeemed_at, member_id")
      .gte("redeemed_at", since)
      .not("redeemed_at", "is", null)
      .order("redeemed_at", { ascending: false })
      .limit(20),
    supabase
      .from("event_registrations" as any)
      .select("id, created_at, event_id, member_id")
      .gte("created_at", since)
      .order("created_at", { ascending: false })
      .limit(20),
  ]);

  const items: Notification[] = [];

  (members.data ?? []).forEach((m: any) => {
    items.push({
      id: `m-${m.id}`,
      type: "member",
      title: "Ny medlem",
      description: `${m.first_name} ${m.last_name} blev medlem`,
      at: m.created_at,
      href: "/af-portal/kunder",
    });
  });

  (benefits.data ?? []).forEach((b: any) => {
    items.push({
      id: `b-${b.id}`,
      type: "benefit",
      title: "Förmån inlöst",
      description: b.benefit_label || "Förmån",
      at: b.redeemed_at,
      href: "/af-portal/rapporter",
    });
  });

  (regs.data ?? []).forEach((r: any) => {
    items.push({
      id: `e-${r.id}`,
      type: "event",
      title: "Eventanmälan",
      description: "Ny anmälan till event",
      at: r.created_at,
      href: "/af-portal/event",
    });
  });

  return items
    .filter((i) => !!i.at)
    .sort((a, b) => +new Date(b.at) - +new Date(a.at))
    .slice(0, 30);
}

const iconMap = { member: UserPlus, benefit: Gift, event: Calendar };

function timeAgo(iso: string): string {
  const sec = Math.floor((Date.now() - +new Date(iso)) / 1000);
  if (sec < 60) return "just nu";
  if (sec < 3600) return `${Math.floor(sec / 60)} min sedan`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} tim sedan`;
  return `${Math.floor(sec / 86400)} dgr sedan`;
}

export function NotificationBell({ variant = "light" }: { variant?: "light" | "dark" } = {}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { session } = useAuth();
  const { data: items = [] } = useQuery({
    queryKey: ["notifications-feed"],
    queryFn: fetchNotifications,
    enabled: !!session,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const [lastRead, setLastRead] = useState<number>(() => {
    try {
      return parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10) || 0;
    } catch {
      return 0;
    }
  });

  const unread = useMemo(
    () => items.filter((i) => +new Date(i.at) > lastRead).length,
    [items, lastRead],
  );

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  function markRead() {
    const now = Date.now();
    setLastRead(now);
    try { localStorage.setItem(STORAGE_KEY, String(now)); } catch {}
  }

  const btnClass =
    variant === "dark"
      ? "text-white/80 hover:bg-white/10"
      : "text-foreground/70 hover:bg-muted border border-border bg-white";

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setOpen((v) => !v); if (!open) markRead(); }}
        className={`relative w-9 h-9 rounded-full inline-flex items-center justify-center transition ${btnClass}`}
        aria-label={`Notiser${unread ? ` (${unread} olästa)` : ""}`}
      >
        <Bell size={17} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[var(--color-gold)] text-[10px] font-bold text-[var(--color-ink)] flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[340px] max-h-[480px] bg-white rounded-xl shadow-2xl border border-border overflow-hidden z-50 flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="font-display font-semibold text-sm">Notiser</div>
            <button onClick={() => setOpen(false)} aria-label="Stäng" className="text-muted-foreground hover:text-foreground">
              <X size={16} />
            </button>
          </div>
          <div className="overflow-y-auto flex-1">
            {!items.length && (
              <div className="px-4 py-10 text-center text-sm text-muted-foreground">Inga händelser senaste 14 dagarna.</div>
            )}
            {items.map((i) => {
              const Icon = iconMap[i.type];
              return (
                <Link
                  key={i.id}
                  to={i.href}
                  onClick={() => setOpen(false)}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-muted transition border-b border-border/50 last:border-b-0"
                >
                  <div className="w-8 h-8 shrink-0 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon size={14} className="text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-semibold text-xs text-foreground">{i.title}</div>
                    <div className="text-xs text-muted-foreground truncate">{i.description}</div>
                    <div className="text-[10px] text-muted-foreground/70 mt-0.5">{timeAgo(i.at)}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
